// Клиент присутствия: отдельный WebSocket к серверу, живущий всё время, пока есть сеть, — и в хотсите тоже.
// Сообщает серверу, чем занят этот браузер (главный экран / хотсит / комната), получает снимки «кто онлайн
// и какие лобби открыты» и приглашения в игру. Обрыв — переподключение с растущей паузой; сервера нет
// (статический хостинг) — молча ничего не показываем.
import type { PresenceMode, PresenceMessage, PresenceSnapshot, InviteMessage } from '../types.ts';

export interface PresenceHandlers {
  onSnapshot(s: PresenceSnapshot): void;
  onInvite(m: InviteMessage): void;
  onError(msg: string): void;
  /** приглашение доставлено адресату */
  onInvited(to: string): void;
  onStatus(connected: boolean): void;
}

export interface PresenceState {
  name: string;
  mode: PresenceMode;
  room: string;
}

function isPresenceMessage(x: unknown): x is PresenceMessage {
  if (!x || typeof x !== 'object') return false;
  const t = (x as { type?: unknown }).type;
  return t === 'presence' || t === 'invite' || t === 'invited' || t === 'error';
}

export class PresenceClient {
  private readonly handlers: PresenceHandlers;
  private readonly id: string;
  private readonly key: string;
  private ws: WebSocket | null = null;
  private retry = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private state: PresenceState = { name: '', mode: 'idle', room: '' };
  private sent: string | null = null;
  connected = false;
  snapshot: PresenceSnapshot | null = null;

  /** key — ключ устройства: с ним сервер запоминает имя для рейтингов (в адрес сокета не кладётся) */
  constructor(id: string, key: string, handlers: PresenceHandlers) {
    this.id = id;
    this.key = key;
    this.handlers = handlers;
  }

  start(): void {
    if (typeof WebSocket === 'undefined' || location.protocol === 'file:') return;
    window.addEventListener('online', () => this.reconnectNow());
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible' && !this.connected) this.reconnectNow(); });
    this.connect();
  }

  private reconnectNow(): void {
    if (this.connected) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.retry = 0;
    this.connect();
  }

  private connect(): void {
    if (this.ws) return;
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const base = location.pathname.replace(/[^/]*$/, '');
    const url = `${proto}//${location.host}${base}presence?id=${encodeURIComponent(this.id)}&name=${encodeURIComponent(this.state.name)}`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.connected = true;
      this.retry = 0;
      this.sent = null;
      this.push();
      this.handlers.onStatus(true);
    };
    ws.onmessage = (ev: MessageEvent<string>) => {
      let m: unknown;
      try {
        m = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (!isPresenceMessage(m)) return;
      switch (m.type) {
        case 'presence': {
          const { type: _t, ...snap } = m;
          this.snapshot = snap;
          this.handlers.onSnapshot(snap);
          return;
        }
        case 'invite': this.handlers.onInvite(m); return;
        case 'invited': this.handlers.onInvited(m.to); return;
        case 'error': this.handlers.onError(m.error); return;
      }
    };
    ws.onclose = () => {
      const was = this.connected;
      this.ws = null;
      this.connected = false;
      this.snapshot = null;
      if (was) this.handlers.onStatus(false);
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    };
  }

  private scheduleReconnect(): void {
    if (this.timer) return;
    // сервера может не быть вовсе (статика) — паузы растут до полуминуты, чтобы не долбить впустую
    const delay = Math.min(30000, 1000 * Math.pow(1.7, this.retry++));
    this.timer = setTimeout(() => {
      this.timer = null;
      this.connect();
    }, delay);
  }

  /** Сообщить серверу, чем занят клиент; повтор того же состояния не отсылается */
  setState(s: PresenceState): void {
    this.state = s;
    this.push();
  }

  private push(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const key = JSON.stringify(this.state);
    if (key === this.sent) return;
    this.sent = key;
    this.ws.send(JSON.stringify({ type: 'state', key: this.key, ...this.state }));
  }

  /** Позвать игрока (по id клиента) в комнату code */
  invite(to: string, code: string): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify({ type: 'invite', to, code }));
    return true;
  }
}
